// frontend/src/pages/admin/PolicyAcknowledgements.js
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, CheckCircle2, Clock, Users, Search, ArrowLeft, FileText } from 'lucide-react';
import { usePolicyAckDashboard } from '../../hooks/usePoliciesData';
import PillToggle from '../../components/UI/PillToggle';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'complete', label: 'Complete' },
];

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 flex items-center gap-3 sm:gap-4">
    <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${tone}`}>
      <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
    </div>
    <div className="min-w-0">
      <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
    </div>
  </div>
);

const PolicyAcknowledgements = () => {
  const { data, isLoading } = usePolicyAckDashboard();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const policies = data?.policies || [];
  const employees = data?.employees || [];
  const summary = data?.summary || { totalEmployees: 0, totalPolicies: 0, fullyAcknowledged: 0, pending: 0 };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const totalPolicies = policies.length;
    return employees.filter((e) => {
      const isComplete = totalPolicies > 0 && e.acknowledgedCount >= totalPolicies;
      if (filter === 'pending' && isComplete) return false;
      if (filter === 'complete' && !isComplete) return false;
      if (!q) return true;
      return [e.name, e.staffId, e.email, e.department]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [employees, policies.length, filter, search]);

  const fmt = (iso) =>
    iso ? new Date(iso).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' }) : '';

  const totalPolicies = policies.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Link to="/admin" aria-label="Back to admin" className="icon-button flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Policy Acknowledgements</h1>
            <p className="text-xs sm:text-sm text-gray-600">
              Who has acknowledged each company policy
            </p>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Active employees" value={summary.totalEmployees} tone="bg-gray-100 text-gray-600" />
        <StatCard icon={FileText} label="Published policies" value={summary.totalPolicies} tone="bg-blue-100 text-blue-600" />
        <StatCard icon={CheckCircle2} label="Fully acknowledged" value={summary.fullyAcknowledged} tone="bg-emerald-100 text-emerald-600" />
        <StatCard icon={Clock} label="With pending" value={summary.pending} tone="bg-amber-100 text-amber-600" />
      </div>

      {/* Per-policy completion */}
      {policies.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Completion by policy</h2>
          <div className="space-y-3">
            {policies.map((p) => (
              <div key={p.key}>
                <div className="flex items-center justify-between text-sm gap-3">
                  <span className="font-medium text-gray-800 truncate">{p.title}</span>
                  <span className="text-gray-500 whitespace-nowrap">
                    {p.acknowledged}/{summary.totalEmployees} · {p.percent}%
                  </span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full transition-[width] duration-300 ${p.percent === 100 ? 'bg-emerald-500' : 'bg-blue-600'}`}
                    style={{ width: `${p.percent}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters + matrix */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center justify-between border-b border-gray-100">
          <PillToggle
            value={filter}
            onChange={setFilter}
            options={FILTERS}
            variant="text"
            theme="admin"
            ariaLabel="Filter employees by acknowledgement status"
          />
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, staff ID, department..."
              className="input-admin w-full pl-9"
              aria-label="Search employees"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">No employees match this view.</div>
        ) : (
          <>
          {/* Mobile: one card per employee (matrix doesn't fit on small screens) */}
          <div className="md:hidden divide-y divide-gray-100">
            {rows.map((e) => {
              const complete = totalPolicies > 0 && e.acknowledgedCount >= totalPolicies;
              return (
                <div key={e.staffId || e.email} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{e.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {e.staffId}{e.email ? ` · ${e.email}` : ''}
                      </p>
                      {e.department && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{e.department}</p>
                      )}
                    </div>
                    {complete ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-gray-500 flex-shrink-0">
                        {e.acknowledgedCount}/{totalPolicies}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {policies.map((p) => {
                      const at = e.acknowledged?.[p.key];
                      return (
                        <div key={p.key} className="flex items-center justify-between gap-2 text-xs">
                          <span className="flex items-center gap-1.5 min-w-0 text-gray-700">
                            {at ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                            ) : (
                              <Clock className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                            )}
                            <span className="truncate">{p.title}</span>
                          </span>
                          <span className="text-gray-400 whitespace-nowrap flex-shrink-0">
                            {at ? fmt(at) : 'Pending'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: full acknowledgement matrix */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell sticky left-0 bg-gray-50 z-10">Employee</th>
                  <th className="table-header-cell">Department</th>
                  {policies.map((p) => (
                    <th key={p.key} className="table-header-cell text-center" title={p.title}>
                      <span className="block max-w-[7rem] mx-auto truncate">{p.title}</span>
                    </th>
                  ))}
                  <th className="table-header-cell text-center">Overall</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((e) => {
                  const complete = totalPolicies > 0 && e.acknowledgedCount >= totalPolicies;
                  return (
                    <tr key={e.staffId || e.email}>
                      <td className="px-6 py-3 sticky left-0 bg-white z-10">
                        <p className="text-sm font-medium text-gray-900">{e.name}</p>
                        <p className="text-xs text-gray-500">{e.staffId}{e.email ? ` · ${e.email}` : ''}</p>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600">{e.department || '—'}</td>
                      {policies.map((p) => {
                        const at = e.acknowledged?.[p.key];
                        return (
                          <td key={p.key} className="px-3 py-3 text-center">
                            {at ? (
                              <span title={`Acknowledged ${fmt(at)}`}>
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" />
                              </span>
                            ) : (
                              <Clock className="w-4 h-4 text-amber-400 inline" aria-label="Pending" />
                            )}
                          </td>
                        );
                      })}
                      <td className="px-6 py-3 text-center">
                        {complete ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                          </span>
                        ) : (
                          <span className="text-xs font-medium text-gray-500">
                            {e.acknowledgedCount}/{totalPolicies}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PolicyAcknowledgements;
